import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminTabs from '../AdminTabs';
import { useAdminSubTab } from '../adminRoute';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import * as er from '@/features/emergency/api';
import { LevelPill, errorInfo } from '@/features/emergency/ui';
import type { ErSettings, MlcCategory, MlcProfile, PayerSchemeAdmin, PewsBand, TriageLevel } from '@/features/emergency/types';

/**
 * Admin Panel → Emergency Dept Customizations (design §5.4), gated on
 * admin:emergency. Every value ships with a working default, so a hospital can
 * go live without opening this. Changes are audited server-side.
 *
 * Triage levels, MLC profiles, payer schemes and paediatric bands are
 * TENANT-WIDE policy (the same answer for the same patient at every site);
 * Observation & flow and Bays are per BRANCH.
 */

function useAdmin<T>(section: string, fn: () => Promise<T>) {
  const { branchVersion } = useAuth();
  return useQuery({ queryKey: [...keys.emergency.admin(section), branchVersion], queryFn: fn });
}
function useSave<V>(section: string, fn: (v: V) => Promise<unknown>, ok = 'Saved') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.emergency.admin(section) });
      qc.invalidateQueries({ queryKey: keys.emergency.meta() });
      toast({ title: ok, variant: 'success' });
    },
    onError: (e) => toast({ title: 'Not saved', description: errorInfo(e).message, variant: 'danger' }),
  });
}

export default function EmergencyAdminTab() {
  const tabs = useAdminSubTab('emergency');
  const sub = tabs.active;
  return (
    <div>
      <AdminTabs state={tabs} />
      {sub === 'levels' && <LevelsTab />}
      {sub === 'flow' && <FlowTab />}
      {sub === 'bays' && <BaysTab />}
      {sub === 'mlc' && <MlcTab />}
      {sub === 'payers' && <PayersTab />}
      {sub === 'pews' && <PewsTab />}
    </div>
  );
}

// ── Triage levels ────────────────────────────────────────────────

function LevelsTab() {
  const q = useAdmin('levels', er.adminLevels);
  const update = useSave('levels', (v: { id: number; body: Partial<TriageLevel> }) => er.adminUpdateLevel(v.id, v.body), 'Level updated (new version)');
  const create = useSave('levels', (b: Partial<TriageLevel>) => er.adminCreateLevel(b), 'Level added');
  const [edit, setEdit] = useState<Record<number, Partial<TriageLevel>>>({});
  const [n, setN] = useState<Partial<TriageLevel>>({ code: '', label: '', color: '', rank: 4, target_door_to_doctor_min: 60, retriage_interval_min: 60 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  return (
    <div className="space-y-3">
      <div className="text-[12px] text-ink-muted">Rank 1 is the most urgent. Editing a level creates a new version; a triage already recorded keeps the targets it was made under.</div>
      {q.data!.map((l) => {
        const e = edit[l.id] ?? {};
        const v = { ...l, ...e };
        return (
          <div key={l.id} className="grid grid-cols-2 items-end gap-2 rounded-xl border border-border bg-card p-3 sm:grid-cols-8">
            <div className="col-span-2 flex items-center gap-2"><LevelPill color={v.color} label={v.label} />{l.mci_only && <Badge variant="neutral">MCI only</Badge>}<Badge variant="neutral">v{l.version}</Badge></div>
            <FormField label="Label"><Input value={v.label} onChange={(x) => setEdit({ ...edit, [l.id]: { ...e, label: x.target.value } })} /></FormField>
            <FormField label="Colour"><Input type="color" value={v.color} onChange={(x) => setEdit({ ...edit, [l.id]: { ...e, color: x.target.value } })} /></FormField>
            <FormField label="Rank"><Input type="number" min={1} value={v.rank} onChange={(x) => setEdit({ ...edit, [l.id]: { ...e, rank: Number(x.target.value) } })} /></FormField>
            <FormField label="Door→doctor (min)"><Input type="number" min={0} value={v.target_door_to_doctor_min} onChange={(x) => setEdit({ ...edit, [l.id]: { ...e, target_door_to_doctor_min: Number(x.target.value) } })} /></FormField>
            <FormField label="Re-triage (min)"><Input type="number" min={1} value={v.retriage_interval_min} onChange={(x) => setEdit({ ...edit, [l.id]: { ...e, retriage_interval_min: Number(x.target.value) } })} /></FormField>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-[12px]"><Checkbox checked={v.active} onCheckedChange={(c) => setEdit({ ...edit, [l.id]: { ...e, active: c === true } })} />Active</label>
              <Button size="sm" disabled={!Object.keys(e).length} loading={update.isPending}
                onClick={() => update.mutate({ id: l.id, body: e }, { onSuccess: () => setEdit((x) => { const y = { ...x }; delete y[l.id]; return y; }) })}>Save</Button>
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-2 items-end gap-2 rounded-xl border border-dashed border-border p-3 sm:grid-cols-7">
        <FormField label="Code"><Input value={n.code} onChange={(x) => setN({ ...n, code: x.target.value.toUpperCase() })} placeholder="ORANGE" /></FormField>
        <FormField label="Label"><Input value={n.label} onChange={(x) => setN({ ...n, label: x.target.value })} /></FormField>
        <FormField label="Colour"><Input type="color" value={n.color || q.data!.at(-1)?.color} onChange={(x) => setN({ ...n, color: x.target.value })} /></FormField>
        <FormField label="Rank"><Input type="number" value={n.rank} onChange={(x) => setN({ ...n, rank: Number(x.target.value) })} /></FormField>
        <FormField label="Door→doctor"><Input type="number" value={n.target_door_to_doctor_min} onChange={(x) => setN({ ...n, target_door_to_doctor_min: Number(x.target.value) })} /></FormField>
        <FormField label="Re-triage"><Input type="number" value={n.retriage_interval_min} onChange={(x) => setN({ ...n, retriage_interval_min: Number(x.target.value) })} /></FormField>
        <Button size="sm" loading={create.isPending} disabled={!n.code || !n.label} onClick={() => create.mutate({ ...n, color: n.color || q.data!.at(-1)?.color })}>Add level</Button>
      </div>
    </div>
  );
}

// ── Observation & flow (per branch) ──────────────────────────────

function FlowTab() {
  const q = useAdmin('settings', er.adminGetSettings);
  const profiles = useAdmin('mlc', er.adminMlcProfiles);
  const save = useSave('settings', (b: Partial<ErSettings>) => er.adminPutSettings(b), 'Settings saved for this branch');
  const [s, setS] = useState<Partial<ErSettings>>({});
  useEffect(() => { if (q.data) setS(q.data); }, [q.data]);
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  const num = (k: keyof ErSettings) => (e: React.ChangeEvent<HTMLInputElement>) => setS({ ...s, [k]: Number(e.target.value) });
  return (
    <div className="max-w-2xl space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="text-[12px] text-ink-muted">These apply to the branch you are signed in to.</div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Mode">
          <NativeSelect value={s.mode ?? 'lite'} onChange={(e) => setS({ ...s, mode: e.target.value as ErSettings['mode'] })}>
            <option value="lite">Casualty-lite (duty doctor, compact board)</option>
            <option value="full">Full ED (bays, dedicated roster)</option>
          </NativeSelect>
        </FormField>
        <FormField label="Default lab priority for ED orders">
          <NativeSelect value={s.default_lab_priority ?? 'stat'} onChange={(e) => setS({ ...s, default_lab_priority: e.target.value as ErSettings['default_lab_priority'] })}>
            <option value="stat">STAT</option><option value="urgent">Urgent</option><option value="routine">Routine</option>
          </NativeSelect>
        </FormField>
        <FormField label="Maximum observation (hours)"><Input type="number" min={1} max={72} value={s.obs_max_hours ?? 24} onChange={num('obs_max_hours')} /></FormField>
        <FormField label="Warn before the limit (minutes)"><Input type="number" min={0} value={s.obs_warn_before_min ?? 60} onChange={num('obs_warn_before_min')} /></FormField>
        <FormField label="Boarding alert after (minutes)"><Input type="number" min={10} value={s.boarding_alert_min ?? 120} onChange={num('boarding_alert_min')} /></FormField>
        <FormField label="Specialist response target (minutes, 'urgent')"><Input type="number" min={5} value={s.consult_response_min ?? 30} onChange={num('consult_response_min')} /></FormField>
        <FormField label="Doctor page: no answer after (minutes)"><Input type="number" min={1} max={30} value={s.doctor_alert_timeout_min ?? 3} onChange={num('doctor_alert_timeout_min')} /></FormField>
        <FormField label="Check for doctor pages every (seconds, 5–60)">
          <Input type="number" min={5} max={60} value={s.doctor_alert_poll_seconds ?? 10} onChange={num('doctor_alert_poll_seconds')}
            title="Lower = the doctor's popup appears sooner, at the cost of more requests to the server" />
        </FormField>
        <FormField label="Patient with an open OP visit arrives in ED" className="col-span-2">
          <NativeSelect value={s.open_op_visit_policy ?? 'leave_open'} onChange={(e) => setS({ ...s, open_op_visit_policy: e.target.value as ErSettings['open_op_visit_policy'] })}>
            <option value="leave_open">Leave the OP visit open (staff are told)</option>
            <option value="auto_close">Close the OP visit automatically</option>
          </NativeSelect>
        </FormField>
        <FormField label="Active MLC profile" className="col-span-2">
          <NativeSelect value={s.mlc_profile_id ?? ''} onChange={(e) => setS({ ...s, mlc_profile_id: e.target.value ? Number(e.target.value) : null })}>
            {(profiles.data ?? []).filter((p) => p.active).map((p) => <option key={p.id} value={p.id}>{p.name} (v{p.version})</option>)}
          </NativeSelect>
        </FormField>
      </div>
      <div className="flex justify-end">
        <Button loading={save.isPending} onClick={() => save.mutate({
          mode: s.mode, obs_max_hours: s.obs_max_hours, obs_warn_before_min: s.obs_warn_before_min, boarding_alert_min: s.boarding_alert_min,
          default_lab_priority: s.default_lab_priority, consult_response_min: s.consult_response_min,
          open_op_visit_policy: s.open_op_visit_policy, mlc_profile_id: s.mlc_profile_id,
          doctor_alert_timeout_min: s.doctor_alert_timeout_min, doctor_alert_poll_seconds: s.doctor_alert_poll_seconds,
        })}>Save</Button>
      </div>
    </div>
  );
}

// ── Bays (per branch) ────────────────────────────────────────────

function BaysTab() {
  const q = useAdmin('bays', er.adminBays);
  const create = useSave('bays', (b: { code: string; zone: string }) => er.adminCreateBay(b), 'Bay added');
  const update = useSave('bays', (v: { id: number; active: boolean }) => er.adminUpdateBay(v.id, { active: v.active }), 'Bay updated');
  const [code, setCode] = useState(''); const [zone, setZone] = useState('major');
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  return (
    <div className="max-w-2xl space-y-3">
      <div className="text-[12px] text-ink-muted">Bays are used in Full ED mode (the board shows a Bay column and one patient per bay). Casualty-lite works without any.</div>
      <div className="flex flex-wrap gap-2">
        {q.data!.length === 0 && <span className="text-[13px] text-ink-muted">No bays yet.</span>}
        {q.data!.map((b) => (
          <div key={b.id} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px]">
            <b>{b.code}</b><Badge variant="neutral">{b.zone}</Badge>{!b.active && <Badge variant="warn">inactive</Badge>}
            <Button size="sm" variant="link" onClick={() => update.mutate({ id: b.id, active: !b.active })}>{b.active ? 'Deactivate' : 'Activate'}</Button>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <FormField label="Code"><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="R1" /></FormField>
        <FormField label="Zone">
          <NativeSelect value={zone} onChange={(e) => setZone(e.target.value)}>
            <option value="resus">Resus</option><option value="major">Major</option><option value="minor">Minor</option><option value="obs">Observation</option><option value="paeds">Paediatric</option>
          </NativeSelect>
        </FormField>
        <Button size="sm" disabled={!code.trim()} loading={create.isPending} onClick={() => create.mutate({ code, zone }, { onSuccess: () => setCode('') })}>Add bay</Button>
      </div>
    </div>
  );
}

// ── MLC profiles ─────────────────────────────────────────────────

function MlcTab() {
  const q = useAdmin('mlc', er.adminMlcProfiles);
  const update = useSave('mlc', (v: { id: number; body: Partial<MlcProfile> }) => er.adminUpdateMlcProfile(v.id, v.body), 'Profile saved (new version)');
  const clone = useSave('mlc', (v: { id: number; name: string }) => er.adminCloneMlcProfile(v.id, v.name), 'Profile cloned');
  const [sel, setSel] = useState<number | null>(null);
  // Clone asks for the name in a dialog — window.prompt() is refused by embedded browsers.
  const [cloning, setCloning] = useState(false);
  const [d, setD] = useState<Partial<MlcProfile> & { categoriesText?: string; fieldsText?: string; rolesText?: string }>({});
  const current = q.data?.find((p) => p.id === sel) ?? q.data?.[0];
  useEffect(() => {
    if (current) setD({
      ...current,
      categoriesText: current.categories.map((c) => `${c.code} | ${c.label} | ${c.mandatory_mlc ? 'mandatory' : ''} | ${c.accident ? 'accident' : ''}`).join('\n'),
      fieldsText: current.register_fields.join(', '),
      rolesText: current.who_may_intimate.join(', '),
    });
  }, [current]);
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  if (!current) return <div className="text-[13px] text-ink-muted">No MLC profile.</div>;

  const parseCategories = (txt: string): MlcCategory[] => txt.split('\n').map((l) => l.split('|').map((x) => x.trim())).filter((p) => p[0] && p[1])
    .map(([code, label, m, a]) => ({ code: code!.toUpperCase(), label: label!, mandatory_mlc: m === 'mandatory', accident: a === 'accident' }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Profile">
          <NativeSelect value={current.id} onChange={(e) => setSel(Number(e.target.value))}>
            {q.data!.map((p) => <option key={p.id} value={p.id}>{p.name} (v{p.version}){p.active ? '' : ' — inactive'}</option>)}
          </NativeSelect>
        </FormField>
        <Button size="sm" variant="ghost" onClick={() => { clone.reset(); setCloning(true); }}>Clone</Button>
      </div>
      {current.notes && <div className="rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">{current.notes}</div>}
      <div className="grid gap-3 rounded-xl border border-border bg-card p-4 lg:grid-cols-2">
        <FormField label="Name"><Input value={d.name ?? ''} onChange={(e) => setD({ ...d, name: e.target.value })} /></FormField>
        <FormField label="State code"><Input value={d.state_code ?? ''} onChange={(e) => setD({ ...d, state_code: e.target.value })} /></FormField>
        <FormField label="Categories — one per line: CODE | Label | mandatory | accident" className="lg:col-span-2">
          <Textarea rows={8} className="font-mono text-[12px]" value={d.categoriesText ?? ''} onChange={(e) => setD({ ...d, categoriesText: e.target.value })} />
        </FormField>
        <FormField label="Required register fields (comma-separated)"><Input value={d.fieldsText ?? ''} onChange={(e) => setD({ ...d, fieldsText: e.target.value })} /></FormField>
        <FormField label="Who may record police intimation (roles, comma-separated)"><Input value={d.rolesText ?? ''} onChange={(e) => setD({ ...d, rolesText: e.target.value })} /></FormField>
        <FormField label="Intimation due within (minutes of arrival)"><Input type="number" value={d.intimation_due_min ?? 60} onChange={(e) => setD({ ...d, intimation_due_min: Number(e.target.value) })} /></FormField>
        <label className="flex items-center gap-2 self-end text-[12px]"><Checkbox checked={!!d.require_brought_by_particulars} onCheckedChange={(c) => setD({ ...d, require_brought_by_particulars: c === true })} />Require who-brought-the-victim particulars for accident categories (or a recorded Good Samaritan refusal)</label>
        <FormField label="Intimation letter wording — placeholders: {{police_station}} {{mlc_number}} {{er_number}} {{patient_description}} {{hospital_name}} {{arrived_at}} {{brought_by}} {{category}} {{incident_place}} {{incident_at}} {{condition}} {{doctor_name}} {{now}}" className="lg:col-span-2">
          <Textarea rows={10} className="font-mono text-[12px]" value={d.intimation_template ?? ''} onChange={(e) => setD({ ...d, intimation_template: e.target.value })} />
        </FormField>
        <label className="flex items-center gap-2 text-[12px]"><Checkbox checked={d.active !== false} onCheckedChange={(c) => setD({ ...d, active: c === true })} />Active</label>
        <div className="flex justify-end">
          <Button loading={update.isPending} onClick={() => update.mutate({ id: current.id, body: {
            name: d.name, state_code: d.state_code, categories: parseCategories(d.categoriesText ?? ''),
            register_fields: (d.fieldsText ?? '').split(',').map((x) => x.trim()).filter(Boolean),
            who_may_intimate: (d.rolesText ?? '').split(',').map((x) => x.trim()).filter(Boolean),
            intimation_due_min: d.intimation_due_min, require_brought_by_particulars: d.require_brought_by_particulars,
            intimation_template: d.intimation_template, active: d.active,
          } })}>Save profile</Button>
        </div>
      </div>
      <TextPromptDialog
        open={cloning}
        title={`Clone ${current.name}?`}
        description="Creates a new profile with the same categories, fields and letter wording, for you to edit."
        label="Name for the copy"
        initialValue={`${current.name} (copy)`}
        confirmLabel="Clone profile"
        pending={clone.isPending}
        error={clone.error ? errorInfo(clone.error).message : null}
        onConfirm={(name) => clone.mutate({ id: current.id, name }, { onSuccess: () => setCloning(false) })}
        onClose={() => setCloning(false)}
      />
    </div>
  );
}

// ── Payer schemes ────────────────────────────────────────────────

function PayersTab() {
  const q = useAdmin('payers', er.adminPayerSchemes);
  const update = useSave('payers', (v: { id: number; body: Partial<PayerSchemeAdmin> }) => er.adminUpdatePayerScheme(v.id, v.body));
  const create = useSave('payers', (b: Partial<PayerSchemeAdmin>) => er.adminCreatePayerScheme(b), 'Scheme added');
  const [n, setN] = useState({ code: '', label: '' });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-ink-muted">Payer category is informational for billing — nothing in the ED path is gated on it. Amounts marked (verify) need checking against the current scheme orders.</div>
      {q.data!.map((s) => <PayerRow key={s.id} s={s} onSave={(body) => update.mutate({ id: s.id, body })} />)}
      <div className="flex items-end gap-2 rounded-xl border border-dashed border-border p-3">
        <FormField label="Code"><Input value={n.code} onChange={(e) => setN({ ...n, code: e.target.value.toLowerCase() })} placeholder="tpa_xyz" /></FormField>
        <FormField label="Label"><Input value={n.label} onChange={(e) => setN({ ...n, label: e.target.value })} /></FormField>
        <Button size="sm" disabled={!n.code || !n.label} loading={create.isPending} onClick={() => create.mutate(n, { onSuccess: () => setN({ code: '', label: '' }) })}>Add scheme</Button>
      </div>
    </div>
  );
}

function PayerRow({ s, onSave }: { s: PayerSchemeAdmin; onSave: (b: Partial<PayerSchemeAdmin>) => void }) {
  const [d, setD] = useState(s);
  return (
    <div className="grid grid-cols-2 items-end gap-2 rounded-xl border border-border bg-card p-3 sm:grid-cols-6">
      <div className="text-[12px]"><b>{s.code}</b></div>
      <FormField label="Label"><Input value={d.label} onChange={(e) => setD({ ...d, label: e.target.value })} /></FormField>
      <FormField label="Covers first (h)"><Input type="number" value={d.covers_first_hours ?? ''} onChange={(e) => setD({ ...d, covers_first_hours: e.target.value === '' ? null : Number(e.target.value) })} /></FormField>
      <FormField label="Cap ₹"><Input type="number" value={d.cap_amount ?? ''} onChange={(e) => setD({ ...d, cap_amount: e.target.value === '' ? null : e.target.value })} /></FormField>
      <label className="flex items-center gap-1 text-[12px]"><Checkbox checked={d.active} onCheckedChange={(c) => setD({ ...d, active: c === true })} />Active</label>
      <Button size="sm" onClick={() => onSave({ label: d.label, covers_first_hours: d.covers_first_hours, cap_amount: d.cap_amount, eligibility_note: d.eligibility_note, active: d.active })}>Save</Button>
      <FormField label="Eligibility note" className="col-span-2 sm:col-span-6"><Input value={d.eligibility_note ?? ''} onChange={(e) => setD({ ...d, eligibility_note: e.target.value })} /></FormField>
    </div>
  );
}

// ── Paediatric bands ─────────────────────────────────────────────

function PewsTab() {
  const q = useAdmin('pews', er.adminPewsBands);
  const update = useSave('pews', (v: { id: number; body: Partial<PewsBand> }) => er.adminUpdatePewsBand(v.id, v.body), 'Band saved (new version)');
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-ink-muted">NEWS2 is validated for adults only, so children under 16 are read against these age-banded normal ranges. They are normal-range flags, not a validated PEWS — confirm with your paediatric lead.</div>
      {q.data!.map((b) => <PewsRow key={b.id} b={b} onSave={(body) => update.mutate({ id: b.id, body })} />)}
    </div>
  );
}

function PewsRow({ b, onSave }: { b: PewsBand; onSave: (body: Partial<PewsBand>) => void }) {
  const [d, setD] = useState(b);
  const n = (k: keyof PewsBand) => (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.target.value === '' ? null : Number(e.target.value) });
  return (
    <div className="grid grid-cols-3 items-end gap-2 rounded-xl border border-border bg-card p-3 sm:grid-cols-8">
      <div className="text-[12px]"><b>{b.label}</b><div className="text-ink-muted">v{b.version}</div></div>
      <FormField label="HR low"><Input type="number" value={d.hr_low ?? ''} onChange={n('hr_low')} /></FormField>
      <FormField label="HR high"><Input type="number" value={d.hr_high ?? ''} onChange={n('hr_high')} /></FormField>
      <FormField label="RR low"><Input type="number" value={d.rr_low ?? ''} onChange={n('rr_low')} /></FormField>
      <FormField label="RR high"><Input type="number" value={d.rr_high ?? ''} onChange={n('rr_high')} /></FormField>
      <FormField label="SBP low"><Input type="number" value={d.sbp_low ?? ''} onChange={n('sbp_low')} /></FormField>
      <FormField label="SpO₂ low"><Input type="number" value={d.spo2_low} onChange={n('spo2_low')} /></FormField>
      <Button size="sm" onClick={() => onSave({ hr_low: d.hr_low, hr_high: d.hr_high, rr_low: d.rr_low, rr_high: d.rr_high, sbp_low: d.sbp_low, spo2_low: d.spo2_low, clinical_basis: d.clinical_basis })}>Save</Button>
      <FormField label="Clinical basis (required)" className="col-span-3 sm:col-span-8"><Input value={d.clinical_basis} onChange={(e) => setD({ ...d, clinical_basis: e.target.value })} /></FormField>
    </div>
  );
}
